import type { ComponentType, ReactNode } from 'react';
import { Alert, AlertDescription } from '@mehir/web-ui/alert';
import { Card, CardContent } from '@mehir/web-ui/card';

interface ScreenHeaderProps {
  icon?: ComponentType<{ className?: string }>;
  title: string;
  /** Rendered on the right — usually the primary action. */
  action?: ReactNode;
}

export const ScreenHeader = ({ icon: Icon, title, action }: ScreenHeaderProps) => (
  <div className="mb-6 flex items-center justify-between">
    <div className="flex items-center gap-2">
      {Icon && <Icon className="h-6 w-6" />}
      <h1 className="text-3xl font-bold">{title}</h1>
    </div>
    {action}
  </div>
);

export const ScreenLoading = () => (
  <Card>
    <CardContent className="p-6">
      <div className="flex h-32 items-center justify-center">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    </CardContent>
  </Card>
);

export const ScreenError = ({ message }: { message: string }) => (
  <Alert variant="destructive">
    <AlertDescription>{message}</AlertDescription>
  </Alert>
);

/**
 * The page container, on its own. Detail and edit screens guard on their
 * record before they can render a header, and each was repeating this div
 * around a hand-written spinner or alert:
 *
 *     if (!id) return <ScreenShell><ScreenError message="…" /></ScreenShell>;
 *     if (isLoading) return <ScreenShell><ScreenLoading /></ScreenShell>;
 *     if (error || !record) return <ScreenShell><ScreenError message="…" /></ScreenShell>;
 *
 * Written out rather than hidden behind one helper on purpose: the third line
 * is what tells TypeScript the record exists from there on.
 */
export const ScreenShell = ({ children }: { children: ReactNode }) => (
  <div className="container mx-auto p-6">{children}</div>
);

interface ScreenStateProps {
  icon?: ComponentType<{ className?: string }>;
  title: string;
  action?: ReactNode;
  isLoading?: boolean;
  error?: unknown;
  errorMessage: string;
  children: ReactNode;
}

/**
 * The header + loading + error scaffolding every list and detail screen opens
 * with. Fifteen of them carried a copy, which is how the spinner ended up
 * three different sizes and a couple of screens lost their heading while
 * loading.
 */
export const ScreenState = ({
  icon,
  title,
  action,
  isLoading,
  error,
  errorMessage,
  children,
}: ScreenStateProps) => (
  <div className="container mx-auto p-6">
    <ScreenHeader icon={icon} title={title} action={isLoading || error ? undefined : action} />
    {isLoading ? <ScreenLoading /> : error ? <ScreenError message={errorMessage} /> : children}
  </div>
);
